import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { 
    Play, 
    Calendar, 
    MapPin,
    Clock,
    Users,
    Music2,
    ExternalLink
} from 'lucide-react';

const performances = [
    {
        id: 1,
        title: 'The Carnival - Live Concert',
        type: 'Concert',
        date: 'December 15, 2024',
        location: 'Mumbai',
        duration: '4 hours',
        guests: '800+',
        image: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/zrs4fm4l_6.jpeg',
        description: 'A spectacular concert at The Carnival featuring pyrotechnics and electrifying performances.',
        highlights: ['Pyrotechnics Show', 'Live Band Setup', 'Guest Interactions'],
    },
    {
        id: 2,
        title: 'Corporate Annual Day 2024',
        type: 'Corporate',
        date: 'November 28, 2024',
        location: 'Bangalore',
        duration: '3 hours',
        guests: '1500+',
        image: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/rz7ko0bw_14.jpeg',
        description: 'High-energy corporate event with a mix of classic rock, pop, and Bollywood numbers.',
        highlights: ['Full Production', 'LED Stage', 'Employee Engagement'],
    },
    {
        id: 3,
        title: 'College Fest Performance',
        type: 'College Fest',
        date: 'October 12, 2024',
        location: 'Mumbai',
        duration: '2.5 hours',
        guests: '5000+',
        image: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/0tqq5lae_7.jpeg',
        description: 'Electrifying performance at one of the largest college festivals.',
        highlights: ['Crowd Interaction', 'Live Mashups', 'Special Effects'],
    },
    {
        id: 4,
        title: 'New Year Eve Bash',
        type: 'Private Party',
        date: 'December 31, 2024',
        location: 'Goa',
        duration: '5 hours',
        guests: '300+',
        image: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/vzl0aowj_2.jpeg',
        description: 'Exclusive beach party with countdown celebration and non-stop entertainment.',
        highlights: ['Beach Setup', 'Countdown Special', 'Fireworks Sync'],
    },
    {
        id: 5,
        title: 'Wedding Sangeet Night',
        type: 'Wedding',
        date: 'September 20, 2024',
        location: 'Delhi',
        duration: '3 hours',
        guests: '200+',
        image: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/ae7t0urh_13.jpeg',
        description: 'Intimate celebration featuring Bollywood classics and heartfelt performances.',
        highlights: ['Bollywood Hits', 'Family Dedications', 'Dance Numbers'],
    },
    {
        id: 6,
        title: 'Product Launch Event',
        type: 'Corporate',
        date: 'August 15, 2024',
        location: 'Hyderabad',
        duration: '2 hours',
        guests: '500+',
        image: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/wvv2ruft_16.jpeg',
        description: 'Premium product launch event with sophisticated entertainment.',
        highlights: ['Brand Integration', 'Custom Jingle', 'VIP Experience'],
    },
];

const PerformancesPage = () => {
    return (
        <main className="min-h-screen pt-24 pb-16 relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute top-1/4 left-0 w-96 h-96 bg-primary/10 rounded-full blur-[150px] pointer-events-none" />
            <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-accent/10 rounded-full blur-[120px] pointer-events-none" />

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Page Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-12"
                >
                    <div className="inline-flex items-center gap-2 mb-4">
                        <Music2 className="w-5 h-5 text-primary" />
                        <span className="text-sm font-body font-medium text-primary uppercase tracking-wider">
                            Performances
                        </span>
                    </div>
                    <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4">
                        Our Live Shows
                    </h1>
                    <p className="text-muted-foreground text-base sm:text-lg font-body max-w-2xl mx-auto">
                        A glimpse into our electrifying performances across India
                    </p>
                </motion.div>

                {/* Performances Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {performances.map((performance, index) => (
                        <motion.div
                            key={performance.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                        >
                            <Card className="card-premium rounded-2xl overflow-hidden h-full group">
                                <CardContent className="p-0 flex flex-col h-full">
                                    {/* Image */}
                                    <div className="relative h-48 overflow-hidden">
                                        <img
                                            src={performance.image}
                                            alt={performance.title}
                                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-card via-card/40 to-transparent" />
                                        
                                        {/* Type Badge */}
                                        <Badge className="absolute top-4 left-4 bg-primary/90 text-foreground border-none">
                                            {performance.type}
                                        </Badge>

                                        {/* Play Button Overlay */}
                                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                            <div className="w-14 h-14 rounded-full bg-primary/90 flex items-center justify-center glow-primary">
                                                <Play className="w-6 h-6 text-foreground fill-foreground ml-1" />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="p-5 flex flex-col flex-1">
                                        <h3 className="font-heading font-bold text-lg text-foreground mb-2">
                                            {performance.title}
                                        </h3>
                                        <p className="text-muted-foreground text-sm font-body mb-4">
                                            {performance.description}
                                        </p>

                                        {/* Meta Info */}
                                        <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground mb-4">
                                            <span className="flex items-center gap-1">
                                                <Calendar className="w-3 h-3 text-primary" />
                                                {performance.date}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <MapPin className="w-3 h-3 text-primary" />
                                                {performance.location}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <Clock className="w-3 h-3 text-primary" />
                                                {performance.duration}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <Users className="w-3 h-3 text-primary" />
                                                {performance.guests} guests
                                            </span>
                                        </div>

                                        {/* Highlights */}
                                        <div className="flex flex-wrap gap-1.5 mt-auto">
                                            {performance.highlights.map((highlight) => (
                                                <span
                                                    key={highlight}
                                                    className="px-2 py-0.5 text-xs font-body bg-muted/50 text-muted-foreground rounded-full border border-border/30"
                                                >
                                                    {highlight}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </div>

                {/* CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                    className="text-center mt-12"
                >
                    <p className="text-muted-foreground font-body mb-4">
                        Want us to perform at your event?
                    </p>
                    <Link to="/book">
                        <Button className="bg-gradient-to-r from-primary to-accent text-foreground font-heading font-semibold px-8 py-6 rounded-xl">
                            Book Your Show
                        </Button>
                    </Link>
                </motion.div>
            </div>
        </main>
    );
};

export default PerformancesPage;
